import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type BaseNotification } from '@strata/react-notifications';

export interface Notification {
    id: string;
    type: (typeof BaseNotification.Types)[keyof typeof BaseNotification.Types];
    linkingId: string;
    payerId: string;
    segmentId: string;
    timestamp: string;
    status: (typeof BaseNotification.Statuses)[keyof typeof BaseNotification.Statuses];
    additionalData: object;
}

export interface NotificationSliceState {
    notifications: Notification[];
    incomingNotification: Notification | null;
    notificationsLoaded: boolean;
}

const initialState: NotificationSliceState = {
    notifications: [],
    incomingNotification: null,
    notificationsLoaded: false,
};

export const notificationSlice = createSlice({
    name: 'notification',
    initialState,
    reducers: {
        addNotifications(state, action: PayloadAction<{ newNotifications: Notification[] }>) {
            const { newNotifications } = action.payload;
            return {
                ...state,
                notifications: [...newNotifications, ...state.notifications],
                notificationsLoaded: true,
            };
        },
        setIncomingNotification(
            state,
            action: PayloadAction<{ incomingNotification: Notification | null }>,
        ) {
            const { incomingNotification } = action.payload;
            return { ...state, incomingNotification };
        },
        setNotificationStatuses(
            state,
            action: PayloadAction<{
                notificationIds: string[];
                status: (typeof BaseNotification.Statuses)[keyof typeof BaseNotification.Statuses];
            }>,
        ) {
            const { notificationIds, status } = action.payload;
            return {
                ...state,
                notifications: state.notifications.map((notification) =>
                    notificationIds.includes(notification.id)
                        ? {
                              ...notification,
                              status,
                          }
                        : notification,
                ),
            };
        },
        removeFromList(state, action: PayloadAction<{ notificationIds: string[] }>) {
            const { notificationIds } = action.payload;
            return {
                ...state,
                notifications: state.notifications.filter(
                    (notification) => !notificationIds.includes(notification.id),
                ),
            };
        },
    },
});

export const {
    addNotifications,
    setIncomingNotification,
    setNotificationStatuses,
    removeFromList,
} = notificationSlice.actions;
